/**
 * Level 3 — the Electrical Station.
 *
 * An antiquated electrical facility: brick walls in inconsistent colours, dusty
 * concrete underfoot, dim fluorescents, and cable and pipe runs along every
 * wall carrying something black. The wiki's structural detail is the one worth
 * building: *hallway dimensions vary dramatically — some sections are extremely
 * tight, as narrow as fourteen inches, while others open into spaces comparable
 * to a mall food court.*
 *
 * So this profile is the only one in the game with no characteristic width. It
 * lays down two or three trunk halls wide enough to drive through, hangs
 * switchgear halls off them on a column pitch, and then threads the whole
 * remaining block of rock with one-cell service crawls that go behind and
 * between everything. Walking here means going from a space you cannot see
 * across to one you have to turn your shoulders in, and back, without ever
 * passing through a door.
 *
 * It is also where the game's entity roster arrives in full. Level 3's
 * documented population is Hounds, Facelings, Deathmoths, Skin-Stealers,
 * Smilers and Clumps — the wiki has them working in organised brigades — which
 * makes this the most dangerous floor in the descent and, because of the
 * assembly lines, by a distance the best supplied.
 */

import { Cell } from '../../core/types';
import { scatterDamp } from './decorate';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  pillarLattice,
  rectsOverlap,
  strokeRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Trunk hall width in cells. The food-court end of the range. */
const TRUNK_WIDTH: readonly [number, number] = [4, 7];
/** Crawl lanes per hundred cells of interior span. */
const CRAWLS_PER_100 = 13;

/** Carve the Electrical Station profile into `site`. */
export function generateStation(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);
  const taken: Rect[] = [];

  // A flat tiling goes down first so the crawls have a label without needing one
  // of their own; everything carved after this overwrites it where they overlap,
  // which is what puts a hall's cells in the hall rather than in the tangle.
  labelServiceBlocks(site, bounds);
  carveTrunks(site, bounds, rng.int(2, 3), taken);
  carveSwitchgear(site, bounds, rng.int(6, 10), taken);
  carveGeneratorRooms(site, bounds, rng.int(3, 6), taken);
  // Last, and only into rock: a crawl that meets a switchgear hall stops being
  // a crawl and becomes a door into it, which is how the two scales get joined
  // without a single corridor being widened.
  carveCrawls(site, bounds);

  // The two operations that read down here are the column in the opening — a
  // 2.8 m crawl with a support planted in it is a squeeze you have to think
  // about — and the doorway into solid brick. Inner rooms are rare: the halls
  // are already full of switchgear that a room could be hiding behind.
  applyWrongness(site, {
    innerRooms: [2, 4],
    doorsToNothing: [12, 20],
    perforatedWalls: [1, 2],
    pillarsInDoorways: [3, 6],
    wallFins: [6, 11],
    tightPillarPatches: [2, 5],
  });

  // The black liquid in the pipes leaks, and the wiki is clear that it is a
  // neurotoxin rather than water. It looks exactly like every other wet patch
  // in the building, which is the point.
  scatterDamp(site, rng.int(18, 30), 5, 22);
}

/**
 * The trunks: full-span halls four to seven cells wide. They are the only thing
 * on the level you can see more than a few metres down, and they run edge to
 * edge so that finding one always tells you something.
 */
function carveTrunks(site: GenSite, bounds: Rect, count: number, taken: Rect[]): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    const horizontal = rng.chance(0.5);
    const width = rng.int(TRUNK_WIDTH[0], TRUNK_WIDTH[1]);
    const crossSpan = horizontal ? bounds.h : bounds.w;
    const crossOrigin = horizontal ? bounds.y : bounds.x;
    const lane = crossOrigin + rng.int(4, Math.max(5, crossSpan - width - 5));
    const r: Rect = horizontal
      ? { x: bounds.x, y: lane, w: bounds.w, h: width }
      : { x: lane, y: bounds.y, w: width, h: bounds.h };
    fillRect(grid, r, Cell.Floor);
    // A column line down the middle of a hall this wide, which is what stops a
    // seven-cell trunk reading as a room with the ends missing.
    if (width >= 5) pillarLattice(grid, inset(r, 1), rng.int(4, 6), 1);
    addRegion(site, r, 'atrium');
    taken.push(r);
  }
}

/**
 * Switchgear halls: walled rooms on a tight column pitch, hung off whatever is
 * already carved. The columns are transformer bays as far as the grid is
 * concerned — they block the middle of a cell, which is exactly what a
 * cabinet-lined aisle does.
 */
function carveSwitchgear(site: GenSite, bounds: Rect, count: number, taken: Rect[]): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 40; tries++) {
      const w = rng.int(9, 16);
      const h = rng.int(8, 14);
      if (bounds.w < w + 4 || bounds.h < h + 4) break;
      const r: Rect = {
        x: rng.int(bounds.x + 1, bounds.x + bounds.w - w - 2),
        y: rng.int(bounds.y + 1, bounds.y + bounds.h - h - 2),
        w,
        h,
      };
      if (taken.some((o) => rectsOverlap(r, o, 2))) continue;
      taken.push(r);

      strokeRect(grid, r, Cell.Wall);
      fillRect(grid, inset(r, 1), Cell.Floor);
      pillarLattice(grid, inset(r, 1), rng.int(3, 4), 1);
      punchDoors(site, r, rng.int(1, 2));
      addRegion(site, inset(r, 1), 'pillar');
      break;
    }
  }
}

/**
 * Generator rooms. Small, walled, one door, and per the wiki the place the
 * Almond Water and the Firesalt are kept — which in this game means they score
 * as closets, and closets are where the supply pass puts a cache.
 */
function carveGeneratorRooms(site: GenSite, bounds: Rect, count: number, taken: Rect[]): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 40; tries++) {
      const w = rng.int(4, 6);
      const h = rng.int(4, 6);
      const r: Rect = {
        x: rng.int(bounds.x + 1, bounds.x + bounds.w - w - 2),
        y: rng.int(bounds.y + 1, bounds.y + bounds.h - h - 2),
        w,
        h,
      };
      if (taken.some((o) => rectsOverlap(r, o, 2))) continue;
      taken.push(r);
      strokeRect(grid, r, Cell.Wall);
      fillRect(grid, inset(r, 1), Cell.Floor);
      punchDoors(site, r, 1);
      addRegion(site, inset(r, 1), 'closet');
      break;
    }
  }
}

/**
 * Openings in a room's wall.
 *
 * A door onto something already carved is preferred and a door onto rock is
 * accepted, because the crawls go in last and most of them will find it. The
 * ones that do not are why this level runs a handful of connectivity repairs a
 * seed — which down here is the right answer anyway: a hole knocked through a
 * brick wall by somebody who needed to get past it belongs on Level 3.
 */
function punchDoors(site: GenSite, r: Rect, count: number): void {
  const { grid, rng } = site;
  const x1 = r.x + r.w - 1;
  const y1 = r.y + r.h - 1;
  for (let n = 0; n < count; n++) {
    let fallback: { x: number; y: number } | null = null;
    let placed = false;
    for (let tries = 0; tries < 12 && !placed; tries++) {
      const side = rng.int(0, 3);
      let x: number;
      let y: number;
      let ox: number;
      let oy: number;
      if (side === 0) {
        x = rng.int(r.x + 1, x1 - 1);
        y = r.y;
        ox = x;
        oy = y - 1;
      } else if (side === 1) {
        x = rng.int(r.x + 1, x1 - 1);
        y = y1;
        ox = x;
        oy = y + 1;
      } else if (side === 2) {
        x = r.x;
        y = rng.int(r.y + 1, y1 - 1);
        ox = x - 1;
        oy = y;
      } else {
        x = x1;
        y = rng.int(r.y + 1, y1 - 1);
        ox = x + 1;
        oy = y;
      }
      fallback ??= { x, y };
      if (!grid.isWalkable(ox, oy)) continue;
      grid.set(x, y, Cell.Doorway);
      placed = true;
    }
    if (!placed && fallback) grid.set(fallback.x, fallback.y, Cell.Doorway);
  }
}

/**
 * The service crawls: one-cell runs threaded through the solid block between
 * everything else.
 *
 * They only ever convert `Cell.Wall`, never anything already carved, so a crawl
 * that meets a switchgear hall passes through its wall and leaves the hall's
 * columns and floor alone. That single rule is what produces the level's
 * signature: the tight sections and the enormous ones are the same corridor.
 * Half of them run the full span so the network is connected before
 * `repairConnectivity` ever sees it.
 */
function carveCrawls(site: GenSite, bounds: Rect): void {
  const { grid, rng } = site;
  const count = Math.round(((bounds.w + bounds.h) * CRAWLS_PER_100) / 100);
  for (let i = 0; i < count; i++) {
    const horizontal = rng.chance(0.5);
    const crossSpan = horizontal ? bounds.h : bounds.w;
    const crossOrigin = horizontal ? bounds.y : bounds.x;
    const lane = crossOrigin + rng.int(1, crossSpan - 2);
    const span = horizontal ? bounds.w : bounds.h;
    const origin = horizontal ? bounds.x : bounds.y;
    const full = rng.chance(0.5);
    const len = full ? span : Math.round(span * rng.range(0.25, 0.7));
    const start = full ? origin : origin + rng.int(0, Math.max(0, span - len));

    for (let t = start; t < start + len && t < origin + span; t++) {
      const x = horizontal ? t : lane;
      const y = horizontal ? lane : t;
      if (grid.at(x, y) === Cell.Wall) grid.set(x, y, Cell.Floor);
    }
  }
}

/** Coarse labels for the block of rock the crawls are threaded through. */
function labelServiceBlocks(site: GenSite, bounds: Rect): void {
  const tiles = 4;
  const tw = Math.floor(bounds.w / tiles);
  const th = Math.floor(bounds.h / tiles);
  for (let j = 0; j < tiles; j++) {
    for (let i = 0; i < tiles; i++) {
      addRegion(
        site,
        {
          x: bounds.x + i * tw,
          y: bounds.y + j * th,
          w: i === tiles - 1 ? bounds.w - i * tw : tw,
          h: j === tiles - 1 ? bounds.h - j * th : th,
        },
        'warren',
      );
    }
  }
}
